// @flow
import zentakeApi, { invalidateMessageCode } from 'api'
import { ResourceSharedContext } from 'components/ResourceFill/providers/ResourceSharedProvider'
import * as React from 'react'
import type { Session } from 'types/session'
import Button from 'ui/atoms/Button'
import Flex from 'ui/atoms/Flex'
import FormInput from 'ui/atoms/FormInput'
import Heading from 'ui/atoms/Heading'
import Modal from 'ui/atoms/Modal'
import Space from 'ui/atoms/Space'
import Text from 'ui/atoms/Text'
import { emailSchema } from 'utils/schemas'

import type { QuestionMetricsPayload } from '@/domain/model/question_metrics'
import { useAddNewQuestionMetric } from '@/queries/questionMetrics/useAddNewQuestionMetric'
import { storageQuestionMetrics } from '@/utils/storageQuestionMetrics/storageQuestionMetrics'
type Props = {
  afterSubmit: () => void,
  onHide: () => void,
  onSave: ({ validate: boolean, submit?: boolean }) => any,
  session: ?Session,
  isPublic: boolean,
}

function SaveForLaterModal({
  afterSubmit,
  onHide,
  onSave,
  isPublic,
  session,
}: Props): React.Node {
  const { mode } = React.useContext(ResourceSharedContext) || {}

  const [submitting, setSubmitting] = React.useState(false)
  const [email, setEmail] = React.useState('')
  const [dirty, setDirty] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')
  const { addNewQuestionMetric } = useAddNewQuestionMetric()

  const validateEmail = React.useCallback(() => {
    try {
      emailSchema.validateSync(email)
      setErrorMessage('')
    } catch (err) {
      setErrorMessage(err.message)
      return false
    }
    return true
  }, [email])

  React.useEffect(() => {
    if (!dirty) return
    validateEmail()
  }, [dirty, validateEmail])

  const onSubmit = React.useCallback(async () => {
    if (submitting) return

    setDirty(true)

    if (isPublic && !validateEmail()) return

    setSubmitting(true)

    const packetResponse = session?.packetResponse
    const response = session?.responses[0]

    const message = packetResponse ? packetResponse?.message : response?.message

    const isInternal = mode === 'internal'

    if (!message && !isPublic && !isInternal) return

    try {
      if (!(await onSave({ validate: false }))) return

      const targetEmail = isPublic ? email : message?.target_email

      if (isPublic) {
        const preferredDeliveryMethod = isPublic
          ? 'email'
          : message?.preferred_delivery_method

        const phoneNumber = isPublic ? null : message?.target_phone

        await zentakeApi.sendMessageToCustomerEditLater({
          targetEmail,
          customer: packetResponse?.customer?.id || response?.customer?.id,
          preferredDeliveryMethod,
          messageType: packetResponse
            ? 'packet_response_request'
            : 'response_request',
          formId: packetResponse ? null : response?.form?.id,
          packetId: packetResponse ? packetResponse?.packet?.id : null,
          is_waiting: false,
          team: packetResponse?.team?.uid || response?.team_details?.uid,
          phoneNumber,
          packet_response: packetResponse ? packetResponse?.id : null,
          form_response: packetResponse ? null : response?.id,
        })

        const getStorageQuestionMetrics = storageQuestionMetrics.get()
        if (Object.keys(getStorageQuestionMetrics)?.length) {
          const formattedStorageQuestionMetrics: QuestionMetricsPayload = {
            ...getStorageQuestionMetrics,
            action: 'CONTINUE-LATER',
          }

          await addNewQuestionMetric(formattedStorageQuestionMetrics)
          storageQuestionMetrics.remove()
        }
      }

      await invalidateMessageCode({
        messageCode: session?.messageCode,
        targetEmail,
      })

      setSubmitting(false)

      onHide()
      afterSubmit()
    } catch (error) {
      setSubmitting(false)
    }
  }, [
    addNewQuestionMetric,
    afterSubmit,
    email,
    isPublic,
    mode,
    onHide,
    onSave,
    session?.messageCode,
    session?.packetResponse,
    session?.responses,
    submitting,
    validateEmail,
  ])

  const renderSubHeading = React.useCallback(() => {
    if (!isPublic) return null

    return (
      <Text $light>
        Enter your email address and we will send you a link to complete your
        forms.
      </Text>
    )
  }, [isPublic])

  const renderEmailField = React.useCallback(() => {
    if (!isPublic) return

    return (
      <div>
        <Text $medium>Email me my link:</Text>
        <FormInput
          $error={Boolean(errorMessage)}
          onChange={(e) => setEmail(e.target.value)}
        />
        {errorMessage && dirty && (
          <Text $red $small>
            {errorMessage}
          </Text>
        )}
      </div>
    )
  }, [dirty, errorMessage, isPublic])

  return (
    <Modal
      show
      closeButton={!submitting && isPublic}
      dismissOnOutsideClick={!submitting}
      onHide={onHide}
      size="small"
    >
      <Space size="large">
        <div>
          <Heading>
            {isPublic
              ? 'Your progress will be saved'
              : 'Are you sure you want to save and exit this form?'}
          </Heading>
          {renderSubHeading()}
        </div>
        {renderEmailField()}
        <Flex $horizontalSpacing="medium" justify="end">
          {!isPublic && <Button onClick={onHide}>Cancel</Button>}
          <Button $primary loading={submitting} onClick={onSubmit}>
            Save
          </Button>
        </Flex>
      </Space>
    </Modal>
  )
}

export default SaveForLaterModal
